import React, { useEffect, useState, useMemo, useRef } from 'react';
import { I, fmt, Sparkline, SprayDiamond } from './shared';
import { api } from './api';

/* ─── PLAYER DRAWER ───────────────────────────────────── */
export function PlayerDrawer({ player, stats, onClose }) {
  const [view, setView] = useState('batting');

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset to batting view when player changes
  useEffect(() => { setView('batting'); }, [player?.playerName]);

  if (!player) return null;

  // Always look up both records by name so we always have the full picture
  const norm = s => String(s||'').replace(/\s+/g,' ').trim().toLowerCase();
  const name = norm(player.playerName || player.name || '');
  const num  = String(player.number || '').trim();

  const batterRecord  = ('AVG' in player ? player : null)
    || stats?.battingSeason?.find(p => norm(p.playerName) === name)
    || stats?.battingSeason?.find(p => String(p.number||'').trim() === num);

  const pitcherRecord = ('ERA' in player && !('AVG' in player) ? player : null)
    || stats?.pitchingSeason?.find(p => norm(p.playerName) === name)
    || stats?.pitchingSeason?.find(p => String(p.number||'').trim() === num);

  const hasPitching = !!(pitcherRecord?.Appearances > 0);
  const displayPlayer = batterRecord || pitcherRecord || player;
  const displayName   = displayPlayer.playerName || displayPlayer.name || '—';
  const displayNumber = displayPlayer.number || '—';

  const subtitle = view === 'batting' && batterRecord
    ? `${batterRecord.Games||'—'} G · ${batterRecord.PA||0} PA`
    : view === 'pitching' && pitcherRecord
    ? `${pitcherRecord.Appearances||0} App · ${pitcherRecord.GS||0} GS`
    : '';

  function handlePrint() {
    const p = batterRecord;
    const pit = pitcherRecord;
    const pos = stats?.fieldingSeason?.find(f =>
      (f.playerName||'').toLowerCase() === (displayName||'').toLowerCase()
    );
    const POS_LIST = ['P','C','1B','2B','3B','SS','LF','CF','RF','DH'];
    let primaryPos = null, maxG = 0;
    if (pos) { for (const k of POS_LIST) { const g = pos['G_'+k]||0; if(g>maxG){maxG=g;primaryPos=k;} } }
    const today = new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'});

    // Team rank helpers
    const teamGames   = stats?.summary?.games ?? 0;
    const paThreshold = 1.5 * teamGames;
    const qualifies   = p && (p.PA||0) >= paThreshold;
    const qualPool    = (stats?.battingSeason||[]).filter(x=>(x.PA||0)>=paThreshold);
    function rankIn(key) {
      if (!qualifies || !p) return null;
      const sorted = [...qualPool].sort((a,b)=>b[key]-a[key]);
      const idx = sorted.findIndex(x=>x.playerName===p?.playerName);
      return idx>=0 ? `${idx+1} / ${sorted.length}` : null;
    }
    function ord(n) { const s=['th','st','nd','rd'],v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); }

    // Last 5 prorated stats
    const last5 = (stats?.teamGameLog||[]).filter(g=>(g.bat_AB||0)>0||(g.bat_R||0)>0||(g.pit_IP||0)>0).slice(-5);
    const l5G = last5.length, games = p?.Games||1;
    const pro = v => v==null?null:Math.round((v/games)*l5G);
    const l5AB  = pro(p?.AB)||0, l5BB = pro(p?.BB)||0, l5HBP = pro(p?.HBP)||0, l5TB = pro(p?.TB)||0;
    const l5H   = p?._spark ? Math.round(p._spark.slice(-5).reduce((a,v)=>a+v,0)/Math.min(5,p._spark.length)*l5AB) : pro(p?.H)||0;
    const l5OBP = (l5AB+l5BB+l5HBP)>0?(l5H+l5BB+l5HBP)/(l5AB+l5BB+l5HBP):null;
    const l5SLG = l5AB>0?l5TB/l5AB:null;
    const l5AVG = l5AB>0?l5H/l5AB:null;
    const l5OPS = l5OBP!=null&&l5SLG!=null?l5OBP+l5SLG:null;
    function fmtAvg(v) { if(v==null)return '—'; return v>=1?v.toFixed(3):'.'+String(v.toFixed(3)).split('.')[1]; }
    function fmtPct(v) { return v==null?'—':v.toFixed(1)+'%'; }
    function fmtIp(v)  { if(!v)return '0.0'; const i=Math.trunc(v),f=Math.round((v-i)*10); return `${i}.${f}`; }

    // Spray chart SVG (black & white friendly)
    const leftPct   = p?.Left_pct   || 0;
    const centerPct = p?.Center_pct || 0;
    const rightPct  = p?.Right_pct  || 0;
    const sprayMax  = Math.max(leftPct, centerPct, rightPct, 1);
    function barH(v) { return Math.round((v/sprayMax)*50); }
    const spraySVG = `<svg viewBox="0 0 120 95" xmlns="http://www.w3.org/2000/svg" width="156" height="123">
      <polygon points="60,10 105,50 60,80 15,50" fill="none" stroke="#bbb" stroke-width="1.5"/>
      <line x1="60" y1="80" x2="60" y2="10" stroke="#eee" stroke-width="0.5"/>
      <line x1="15" y1="50" x2="105" y2="50" stroke="#eee" stroke-width="0.5"/>
      <rect x="8"  y="${50-barH(leftPct)}"   width="16" height="${barH(leftPct)}"   fill="#111" opacity="0.85"/>
      <rect x="52" y="${50-barH(centerPct)}" width="16" height="${barH(centerPct)}" fill="#111" opacity="0.85"/>
      <rect x="96" y="${50-barH(rightPct)}"  width="16" height="${barH(rightPct)}"  fill="#111" opacity="0.85"/>
      <text x="16"  y="89" text-anchor="middle" font-size="8" font-family="Arial" font-weight="700" fill="#111">L ${leftPct.toFixed(0)}%</text>
      <text x="60"  y="89" text-anchor="middle" font-size="8" font-family="Arial" font-weight="700" fill="#111">C ${centerPct.toFixed(0)}%</text>
      <text x="104" y="89" text-anchor="middle" font-size="8" font-family="Arial" font-weight="700" fill="#111">R ${rightPct.toFixed(0)}%</text>
    </svg>`;

    const cell = (label, value) => `
      <td style="padding:4px 3px;text-align:center;border-right:1px solid #ddd;border-bottom:1px solid #ddd">
        <div style="font-size:7px;color:#111;font-weight:700;text-transform:uppercase;margin-bottom:1px">${label}</div>
        <div style="font-size:11px;font-weight:700;font-family:monospace;color:#111">${value ?? '—'}</div>
      </td>`;

    const html = `<!DOCTYPE html><html><head><title>Scouting Report — ${displayName}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:10px; color:#111; padding:0.35in; }
      @page { margin:0; size:letter portrait; }
      table { border-collapse:collapse; width:100%; table-layout:fixed; }
      .sl { font-size:7.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:#666; border-bottom:2px solid #111; padding-bottom:2px; margin-bottom:5px; }
      .section { margin-bottom:8px; }
    </style></head><body>

    <!-- Header -->
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #111;padding-bottom:6px;margin-bottom:8px">
      <div>
        <div style="font-size:20px;font-weight:900;letter-spacing:-0.02em;text-transform:uppercase">#${displayNumber} ${displayName}</div>
        <div style="font-size:10px;color:#555;margin-top:1px">${[primaryPos, p?`${p.Games||0} G · ${p.PA||0} PA`:null].filter(Boolean).join('  ·  ')}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:8px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#888">Scouting Nexus</div>
        <div style="font-size:8px;color:#aaa;margin-top:1px">${today}</div>
      </div>
    </div>

    ${p ? `
    <!-- Slash hero -->
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:8px">
      ${[['AVG',fmtAvg(p.AVG)],['OBP',fmtAvg(p.OBP)],['SLG',fmtAvg(p.SLG)],['OPS',fmtAvg(p.OPS)]].map(([k,v])=>`
        <div style="border:1px solid #ddd;border-radius:4px;padding:5px 8px;background:#fafafa;text-align:center">
          <div style="font-size:7px;font-weight:700;text-transform:uppercase;color:#111">${k}</div>
          <div style="font-size:20px;font-weight:900;font-family:monospace;color:#111;white-space:nowrap">${v}</div>
        </div>`).join('')}
    </div>

    <!-- Last 5 -->
    <div class="section">
      <div class="sl">Last 5 Games (prorated)</div>
      <table><tbody><tr style="background:#f5f5f5">
        ${[['AVG',fmtAvg(l5AVG)],['OBP',fmtAvg(l5OBP)],['SLG',fmtAvg(l5SLG)],['OPS',fmtAvg(l5OPS)],['2B',pro(p?.['2B'])],['3B',pro(p?.['3B'])],['HR',pro(p?.HR)],['SB',pro(p?.SB)]].map(([k,v])=>cell(k,v)).join('')}
      </tr></tbody></table>
    </div>

    <!-- Batting stats + Spray side by side -->
    <div style="display:grid;grid-template-columns:1fr 160px;gap:12px;margin-bottom:8px;align-items:start">
      <div>
        <div class="section">
          <div class="sl">Batting Statistics</div>
          <table><tbody>
            <tr style="background:#f5f5f5">${[['G',p.Games],['PA',p.PA],['AB',p.AB],['H',p.H],['2B',p['2B']],['3B',p['3B']],['HR',p.HR],['TB',p.TB]].map(([k,v])=>cell(k,v)).join('')}</tr>
            <tr>${[['R',p.R],['RBI',p.RBI],['BB',p.BB],['HBP',p.HBP],['SO',p.SO],['SB',p.SB],['CS',p.CS],['ISO',fmtAvg(p.ISO)]].map(([k,v])=>cell(k,v)).join('')}</tr>
          </tbody></table>
        </div>
        <div class="section">
          <div class="sl">Advanced Rates</div>
          <table><tbody><tr style="background:#f5f5f5">
            ${[['BB%',fmtPct(p.BB_pct)],['K%',fmtPct(p.SO_pct)],['Bunt%',fmtPct(p.Bunt_pct)]].map(([k,v])=>cell(k,v)).join('')}
          </tr></tbody></table>
        </div>
      </div>
      <div style="text-align:center">
        <div style="font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#111;margin-bottom:3px">Spray Chart</div>
        ${spraySVG}
      </div>
    </div>

    <!-- Team Rank -->
    <div class="section">
      <div class="sl">Team Rank ${qualifies?`(among ${qualPool.length} qualified · min ${Math.ceil(paThreshold)} PA)`:'(not yet qualified)'}</div>
      <table><tbody><tr style="background:#f5f5f5">
        ${['AVG','OBP','SLG','OPS','H','HR','RBI','SB','BB_pct','SO_pct'].map(k=>{
          const label = k==='BB_pct'?'BB%':k==='SO_pct'?'K%':k;
          const dir = k==='SO_pct'?'asc':'desc';
          if (!qualifies) return cell(label,'—');
          const sorted = [...qualPool].sort((a,b)=>dir==='desc'?b[k]-a[k]:a[k]-b[k]);
          const idx = sorted.findIndex(x=>x.playerName===p?.playerName);
          const r = idx>=0?`${ord(idx+1)} / ${sorted.length}`:null;
          return cell(label, r||'—');
        }).join('')}
      </tr></tbody></table>
    </div>` : ''}

    <!-- Pitching -->
    <div class="section">
      <div class="sl">Pitching</div>
      ${pit && pit.Appearances > 0 ? `
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:5px;margin-bottom:5px">
          ${[['ERA',(pit.ERA||0).toFixed(2)],['WHIP',(pit.WHIP||0).toFixed(2)],['K/7',(pit.K_per_7||0).toFixed(2)]].map(([k,v])=>`
            <div style="border:1px solid #ddd;border-radius:3px;padding:4px 6px;background:#fafafa;text-align:center">
              <div style="font-size:7px;font-weight:700;text-transform:uppercase;color:#111">${k}</div>
              <div style="font-size:14px;font-weight:900;font-family:monospace;color:#111">${v}</div>
            </div>`).join('')}
        </div>
        <table><tbody><tr style="background:#f5f5f5">
          ${[['App',pit.Appearances],['IP',fmtIp(pit.IP)],['BF',pit.BF],['H',pit.H],['R',pit.R],['ER',pit.ER],['BB',pit.BB],['K',pit.SO],['HBP',pit.HBP],['Strike%',fmtPct(pit.StrikePct)],['K/BB',pit.BB>0?(pit.SO/pit.BB).toFixed(1):'—']].map(([k,v])=>cell(k,v)).join('')}
        </tr></tbody></table>
      ` : `<div style="color:#111;font-style:italic;font-size:10px;padding:4px 0">No pitching appearances this season</div>`}
    </div>

    <!-- Fielding -->
    <div class="section">
      <div style="border-bottom:2px solid #111;padding-bottom:2px;margin-bottom:5px">
        <span style="font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#111">Fielding — Games by Position</span>
      </div>
      ${pos ? `
        <div style="display:flex;gap:0;align-items:stretch">
          <table style="flex:1;border:1px solid #ddd"><thead>
            <tr>${POS_LIST.map(k=>`<th style="padding:3px 4px;text-align:center;font-size:7.5px;font-weight:700;background:#f5f5f5;border-right:1px solid #ddd;color:#111">${k}</th>`).join('')}</tr>
          </thead><tbody><tr>
            ${POS_LIST.map(k=>{const g=pos['G_'+k]||0;return `<td style="padding:3px 4px;text-align:center;font-size:11px;font-weight:700;font-family:monospace;border-right:1px solid #ddd;color:#111;${k===primaryPos?'background:#111;color:#fff !important':''}">${g||'—'}</td>`;}).join('')}
          </tr></tbody></table>
          <div style="border:1px solid #ddd;border-left:3px solid #111;padding:6px 14px;text-align:center;min-width:60px;background:#f5f5f5;display:flex;flex-direction:column;justify-content:center;align-items:center">
            <div style="font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#111;margin-bottom:3px">Errors</div>
            <div style="font-size:22px;font-weight:900;font-family:monospace;color:#111;line-height:1">${pos.E||0}</div>
          </div>
        </div>
      ` : `<div style="color:#111;font-style:italic;font-size:10px;padding:4px 0">No fielding data</div>`}
    </div>

    <!-- Footer -->
    <div style="border-top:1px solid #eee;padding-top:4px;margin-top:6px;font-size:7px;color:#aaa;display:flex;justify-content:space-between">
      <span>Generated by Scouting Nexus</span>
      <span>scoutnexus.ncapatina.com</span>
    </div>

    <script>window.onload=function(){window.print();window.close();}</script>
    </body></html>`;

    const w = window.open('','_blank','width=850,height=1100');
    w.document.write(html);
    w.document.close();
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose}/>
      <aside className="drawer" role="dialog" aria-label={`${displayName} details`}>
        <div className="drawer-h">
          <div className="drawer-num">{displayNumber}</div>
          <div className="drawer-title">
            <h3>{displayName}</h3>
            <p>{subtitle}</p>
          </div>
          <button onClick={handlePrint} style={{
            padding:'6px 10px', fontSize:9, fontWeight:800,
            letterSpacing:'0.08em', textTransform:'uppercase',
            background:'var(--surface-2)', border:'1px solid var(--border)',
            borderRadius:6, color:'var(--text-2)', cursor:'pointer',
            whiteSpace:'nowrap', flexShrink:0,
          }}>Print Scouting Report</button>
          <button className="icon-btn" onClick={onClose}><I.X/></button>
        </div>

        {/* Batting / Pitching toggle */}
        <div style={{display:'flex', borderBottom:'1px solid var(--border)', background:'var(--surface)'}}>
          {['batting','pitching'].map(v => (
            <button key={v} onClick={()=>setView(v)} style={{
              flex:1, padding:'10px 0', fontSize:12, fontWeight:700,
              textTransform:'uppercase', letterSpacing:'0.08em',
              color: view===v ? 'var(--text)' : 'var(--text-3)',
              borderBottom: view===v ? '2px solid var(--red)' : '2px solid transparent',
              transition:'color 120ms, border-color 120ms',
            }}>
              {v}
            </button>
          ))}
        </div>

        <div className="drawer-body">
          {view === 'batting' && (
            batterRecord
              ? <><BatterDetail p={batterRecord} stats={stats}/><FieldingDetail p={batterRecord} stats={stats}/></>
              : <div className="empty" style={{marginTop:24}}><h3>No batting appearances</h3></div>
          )}
          {view === 'pitching' && (
            hasPitching
              ? <PitcherDetail p={pitcherRecord}/>
              : <div className="empty" style={{marginTop:24}}><h3>No pitching appearances</h3></div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ─── Print Scouting Report ───────────────────────────── */
function SlashStat({ k, v, hi }) {
  return (
    <div style={{padding: 10, borderRadius: 10, background: hi ? 'var(--navy-900)' : 'var(--surface-2)', border: '1px solid var(--border)', color: hi ? '#fff' : 'inherit'}}>
      <div style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: hi ? 'rgba(255,255,255,0.6)' : 'var(--text-3)'}}>{k}</div>
      <div style={{fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 600, marginTop: 1, letterSpacing: '-0.01em'}}>{v}</div>
    </div>
  );
}

function WideStatBar({ items, large }) {
  return (
    <div style={{display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden'}}>
      {items.map(([k, v]) => (
        <div key={k} style={{background: 'var(--surface)', padding: large ? '10px 3px' : '7px 3px', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', minWidth: 0}}>
          <span style={{fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)'}}>{k}</span>
          <span className="mono" style={{fontSize: large ? 16 : 12, fontWeight: 600}}>{v ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

function DetailGrid({ items }) {
  return (
    <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden'}}>
      {items.map(([k, v]) => (
        <div key={k} style={{background: 'var(--surface)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2}}>
          <span style={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)'}}>{k}</span>
          <span className="mono" style={{fontSize: 14, fontWeight: 600}}>{v ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Batter detail ───────────────────────────────────── */
function BatterDetail({ p, stats }) {
  const teamGames     = stats?.summary?.games ?? stats?.gamesProcessed ?? 0;
  const paThreshold   = 1.5 * teamGames;
  const qualifies     = (p.PA || 0) >= paThreshold;
  const qualifiedPool = (stats?.battingSeason || []).filter(x => (x.PA || 0) >= paThreshold);

  function rankIn(key, dir = 'desc') {
    if (!qualifies) return null;
    const sorted = [...qualifiedPool].sort((a, b) => dir === 'desc' ? (b[key] - a[key]) : (a[key] - b[key]));
    const idx = sorted.findIndex(x => x.playerId === p.playerId || x.playerName === p.playerName);
    return idx >= 0 ? { rank: idx + 1, of: sorted.length } : null;
  }

  const ranks = { AVG: rankIn('AVG'), OBP: rankIn('OBP'), SLG: rankIn('SLG'), OPS: rankIn('OPS') };
  function ord(n) { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); }

  const last5Games = (stats?.teamGameLog || [])
    .filter(g => (g.bat_AB || 0) > 0 || (g.bat_R || 0) > 0 || (g.pit_IP || 0) > 0)
    .slice(-5);

  return (
    <>
      {/* Slash line hero */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10}}>
        <SlashStat k="AVG" v={fmt.avg(p.AVG)}/>
        <SlashStat k="OBP" v={fmt.avg(p.OBP)}/>
        <SlashStat k="SLG" v={fmt.avg(p.SLG)}/>
        <SlashStat k="OPS" v={fmt.avg(p.OPS)} hi/>
      </div>

      {/* Statistics */}
      <div className="card" style={{margin: 0, marginBottom: 10}}>
        <div className="card-h" style={{marginBottom: 8}}><h2>Statistics</h2></div>
        <WideStatBar items={[
          ['G', p.Games], ['PA', p.PA], ['AB', p.AB],
          ['R', p.R], ['H', p.H], ['RBI', p.RBI],
          ['2B', p['2B']], ['3B', p['3B']], ['HR', p.HR],
          ['BB', p.BB], ['HBP', p.HBP], ['SO', p.SO],
          ['SB', p.SB], ['CS', p.CS], ['TB', p.TB],
        ]} large/>
      </div>

      {/* Player Insights */}
      <div className="card" style={{margin: 0, marginBottom: 10}}>
        <div className="card-h" style={{marginBottom: 8}}><h2>Player Insights</h2></div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden'}}>
          {[
            ['BB%',   fmt.pct(p.BB_pct)],
            ['SO%',   fmt.pct(p.SO_pct)],
            ['ISO',   fmt.avg(p.ISO)],
            ['Bunt%', fmt.pct(p.Bunt_pct)],
          ].map(([k, v]) => (
            <div key={k} style={{background: 'var(--surface)', padding: '7px 6px', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', minWidth: 0}}>
              <span style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)'}}>{k}</span>
              <span className="mono" style={{fontSize: 14, fontWeight: 600}}>{v ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Team Rank */}
      <div className="card" style={{margin: 0, marginBottom: 10}}>
        <div className="card-h" style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8}}>
          <h2>Team Rank</h2>
          <span style={{fontSize: 10, color: 'var(--text-3)'}}>
            {qualifies
              ? `Among ${qualifiedPool.length} qualified · min ${paThreshold.toFixed(1)} PA`
              : `Not qualified · needs ${Math.ceil(paThreshold)} PA (has ${p.PA || 0})`}
          </span>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden'}}>
          {['AVG','OBP','SLG','OPS'].map(k => {
            const r = ranks[k];
            return (
              <div key={k} style={{background: 'var(--surface)', padding: '7px 6px', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', minWidth: 0}}>
                <span style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)'}}>{k}</span>
                {r ? (
                  <span className="mono" style={{fontSize: 14, fontWeight: 600}}>
                    {ord(r.rank)}<span style={{fontSize: 10, color: 'var(--text-3)', fontWeight: 500}}> / {r.of}</span>
                  </span>
                ) : (
                  <span className="mono" style={{fontSize: 14, fontWeight: 600, color: 'var(--text-3)'}}>—</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hit rate sparkline */}
      {p._spark && (
        <div className="card" style={{margin: 0, marginBottom: 10}}>
          <div className="card-h" style={{marginBottom: 6}}><h2>Hit rate · last {p._spark.length} games</h2></div>
          <Sparkline data={p._spark} w={420} h={48}/>
          <div style={{fontSize: 11, color: 'var(--text-3)', marginTop: 2}}>Latest: {fmt.avg(p._spark[p._spark.length - 1])}</div>
        </div>
      )}

      {/* Last 5 games — prorated player stats */}
      {last5Games.length > 0 && <Last5PlayerStats player={p} last5={last5Games}/>}

      {/* Spray chart */}
      <div className="card" style={{margin: 0}}>
        <div className="card-h" style={{marginBottom: 6}}><h2>Spray Chart</h2></div>
        <div className="diamond-wrap">
          <SprayDiamond left={p.Left_pct} center={p.Center_pct} right={p.Right_pct}/>
        </div>
      </div>
    </>
  );
}

/* ─── Last 5 player stats (prorated) ─────────────────── */
function Last5PlayerStats({ player: p, last5 }) {
  const games   = p.Games || 1;
  const last5G  = last5.length;
  const pro     = v => (v == null ? null : Math.round((v / games) * last5G));

  // Use _spark for last-5 AVG if available (it's per-game hit rate)
  const last5Spark = p._spark ? p._spark.slice(-5) : null;
  const last5AB    = pro(p.AB) || 0;
  const last5H     = last5Spark
    ? Math.round(last5Spark.reduce((a,v) => a+v, 0) / last5Spark.length * last5AB)
    : pro(p.H) || 0;
  const last5BB    = pro(p.BB) || 0;
  const last5HBP   = pro(p.HBP) || 0;
  const last5TB    = pro(p.TB) || 0;
  const last52B    = pro(p['2B']);
  const last53B    = pro(p['3B']);
  const last5HR    = pro(p.HR);
  const last5SB    = pro(p.SB);

  const last5AVG = last5AB > 0 ? last5H / last5AB : null;
  const obpDen   = last5AB + last5BB + last5HBP;
  const last5OBP = obpDen > 0 ? (last5H + last5BB + last5HBP) / obpDen : null;
  const last5SLG = last5AB > 0 ? last5TB / last5AB : null;
  const last5OPS = last5OBP != null && last5SLG != null ? last5OBP + last5SLG : null;

  return (
    <div className="card" style={{margin: 0, marginBottom: 10}}>
      <div className="card-h" style={{marginBottom: 8}}>
        <h2>Last 5 games</h2>
        <span style={{fontSize: 10, color: 'var(--text-3)'}}>Prorated from season</span>
      </div>
      {/* W/L result pills */}
      <div style={{display: 'flex', gap: 4, marginBottom: 10}}>
        {last5.map((g, i) => {
          const res = g.Result || (g.bat_R > g.pit_R ? 'W' : g.bat_R < g.pit_R ? 'L' : 'T');
          return (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 2, padding: '5px 8px', borderRadius: 8, flex: 1,
              background: res === 'W' ? 'var(--good-tint)' : res === 'L' ? 'var(--red-tint)' : 'var(--surface-2)',
              border: `1px solid ${res === 'W' ? 'color-mix(in srgb, var(--good) 30%, transparent)' : res === 'L' ? 'color-mix(in srgb, var(--red) 30%, transparent)' : 'var(--border)'}`,
            }}>
              <span style={{fontSize: 11, fontWeight: 800, fontFamily: 'var(--font-display)', color: res === 'W' ? 'var(--good)' : res === 'L' ? 'var(--red)' : 'var(--text-3)'}}>{res}</span>
              <span className="mono" style={{fontSize: 10, color: 'var(--text-2)'}}>{g.bat_R}–{g.pit_R}</span>
            </div>
          );
        })}
      </div>
      {/* Stat grid: AVG, OBP, SLG, OPS, 2B, 3B, HR, SB */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden'}}>
        {[
          ['AVG', fmt.avg(last5AVG)],
          ['OBP', fmt.avg(last5OBP)],
          ['SLG', fmt.avg(last5SLG)],
          ['OPS', fmt.avg(last5OPS)],
          ['2B',  last52B ?? '—'],
          ['3B',  last53B ?? '—'],
          ['HR',  last5HR ?? '—'],
          ['SB',  last5SB ?? '—'],
        ].map(([k, v]) => (
          <div key={k} style={{background: 'var(--surface)', padding: '7px 6px', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', minWidth: 0}}>
            <span style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)'}}>{k}</span>
            <span className="mono" style={{fontSize: 13, fontWeight: 600}}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Fielding detail ─────────────────────────────────── */
function FieldingDetail({ p, stats }) {
  const f = (stats?.fieldingSeason || []).find(x => x.playerName === p.playerName);
  if (!f) return null;
  const PO = f.PO || 0, A = f.A || 0, E = f.E || 0;
  const TC = PO + A + E;
  const FPCT = TC > 0 ? (PO + A) / TC : 0;

  const POS_LIST = [
    { key: 'P', label: 'P' }, { key: 'C', label: 'C' },
    { key: '1B', label: '1B' }, { key: '2B', label: '2B' },
    { key: '3B', label: '3B' }, { key: 'SS', label: 'SS' },
    { key: 'LF', label: 'LF' }, { key: 'CF', label: 'CF' },
    { key: 'RF', label: 'RF' }, { key: 'DH', label: 'DH' },
    { key: 'F10', label: 'F10' },
  ];
  const positions = POS_LIST
    .map(pos => ({ ...pos, games: f['G_' + pos.key] || 0 }))
    .filter(pos => pos.games > 0);
  if (!positions.length && TC === 0) return null;

  const maxGames   = Math.max(1, ...positions.map(x => x.games));
  const fielding   = positions.filter(x => x.key !== 'DH' && x.key !== 'F10');
  const primaryKey = fielding.reduce((best, x) => (!best || x.games > best.games ? x : best), null)?.key;

  return (
    <div className="card" style={{margin: 0, marginBottom: 10, marginTop: 10}}>
      <div className="card-h" style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8}}>
        <h2>Fielding</h2>
        {primaryKey && <span style={{fontSize: 11, color: 'var(--text-3)'}}>Primary · <span style={{color: 'var(--text)', fontWeight: 700}}>{primaryKey}</span></span>}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 10}}>
        {[['TC', TC], ['F%', fmt.avg(FPCT)], ['E', E]].map(([k, v]) => (
          <div key={k} style={{background: 'var(--surface)', padding: '7px 6px', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', minWidth: 0}}>
            <span style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)'}}>{k}</span>
            <span className="mono" style={{fontSize: 14, fontWeight: 600}}>{v}</span>
          </div>
        ))}
      </div>
      {positions.length > 0 && (
        <>
          <div style={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)', marginBottom: 5}}>Games by position</div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
            {positions.map(pos => {
              const isPrimary = pos.key === primaryKey;
              const pct = (pos.games / maxGames) * 100;
              return (
                <div key={pos.key} style={{display: 'grid', gridTemplateColumns: '30px 1fr 24px', alignItems: 'center', gap: 8, fontSize: 11}}>
                  <div className="mono" style={{fontWeight: isPrimary ? 700 : 500, color: isPrimary ? 'var(--red)' : 'var(--text-2)'}}>{pos.label}</div>
                  <div style={{height: 8, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 2, overflow: 'hidden'}}>
                    <div style={{height: '100%', width: `${pct}%`, background: isPrimary ? 'var(--red)' : 'var(--text-3)', opacity: isPrimary ? 1 : 0.45}}/>
                  </div>
                  <div className="mono" style={{textAlign: 'right', fontWeight: isPrimary ? 700 : 500, color: isPrimary ? 'var(--text)' : 'var(--text-2)'}}>{pos.games}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Pitcher detail ──────────────────────────────────── */
function PitcherDetail({ p }) {
  const kbb = p.BB > 0 ? (p.SO / p.BB).toFixed(2) : '—';
  const kbbVal = p.BB > 0 ? p.SO / p.BB : 0;
  const kbbColor = kbbVal >= 3 ? 'var(--good)' : kbbVal >= 2 ? 'var(--text-2)' : 'var(--red)';

  return (
    <>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 18}}>
        <SlashStat k="ERA"  v={(p.ERA  || 0).toFixed(2)} hi/>
        <SlashStat k="WHIP" v={(p.WHIP || 0).toFixed(2)}/>
        <SlashStat k="K/7"  v={(p.K_per_7 || 0).toFixed(2)}/>
      </div>

      {/* Statistics — single row table */}
      <div className="card" style={{margin: 0, marginBottom: 14}}>
        <div className="card-h"><h2>Statistics</h2></div>
        <div style={{overflowX:'auto'}}>
          <table className="dt" style={{width:'100%',fontSize:11}}>
            <thead><tr>
              {[['App','Appearances'],['IP','IP'],['BF','BF'],['H','H'],['R','R'],['ER','ER'],['BB','BB'],['K','SO'],['HBP','HBP'],['WP','WP']].map(([label])=>(
                <th key={label} style={{padding:'5px 4px'}}>{label}</th>
              ))}
            </tr></thead>
            <tbody><tr>
              {[['App',p.Appearances],['IP',fmt.ip(p.IP)],['BF',p.BF],['H',p.H],['R',p.R],['ER',p.ER],['BB',p.BB],['K',p.SO],['HBP',p.HBP],['WP',p.WP]].map(([label,val])=>(
                <td key={label} className="rate" style={{padding:'5px 4px'}}>{val ?? '—'}</td>
              ))}
            </tr></tbody>
          </table>
        </div>
      </div>

      {/* Strike rate with label */}
      <div className="card" style={{margin: 0, marginBottom: 14}}>
        <div className="card-h">
          <h2>Strike rate</h2>
          <span style={{fontFamily:'var(--font-mono)',fontSize:14,fontWeight:700,color:'var(--text)'}}>{fmt.pct(p.StrikePct)}</span>
        </div>
        <div style={{height: 18, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden'}}>
          <div style={{height: '100%', width: `${p.StrikePct || 0}%`, background: 'linear-gradient(90deg, var(--text-2), var(--red))'}}/>
        </div>
        <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text-3)'}}>
          <span>0%</span><span>League avg ~60%</span><span>100%</span>
        </div>
      </div>

      {/* K/BB ratio */}
      <div className="card" style={{margin: 0}}>
        <div className="card-h">
          <h2>K/BB ratio</h2>
          <span style={{fontFamily:'var(--font-mono)',fontSize:14,fontWeight:700,color:kbbColor}}>{kbb}</span>
        </div>
        <div style={{height:14,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:4,overflow:'hidden',position:'relative'}}>
          <div style={{height:'100%',width:`${Math.min(100,(kbbVal/5)*100)}%`,background:kbbColor,opacity:0.8,borderRadius:4}}/>
          {/* Reference lines */}
          <div style={{position:'absolute',left:'40%',top:0,height:'100%',borderLeft:'1px dashed var(--border-strong)'}}/>
          <div style={{position:'absolute',left:'60%',top:0,height:'100%',borderLeft:'1px dashed var(--good)',opacity:0.5}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:11,color:'var(--text-3)'}}>
          <span>0</span><span>Gray = 2.0</span><span>Green = 3.0</span>
        </div>
      </div>
    </>
  );
}

/* ─── BOX SCORE DRAWER ─────────────────────────────────── */
export function BoxScoreDrawer({ game, team, stats: statsProp, onOpenPlayer, onClose }) {
  const stats = statsProp || game?._stats || null;
  const [boxData, setBoxData]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [inlinePlayer, setInlinePlayer] = useState(null);
  const statsRef = useRef(stats);
  useEffect(() => { statsRef.current = statsProp || game?._stats || null; }, [statsProp, game]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { inlinePlayer ? setInlinePlayer(null) : onClose(); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, inlinePlayer]);

  useEffect(() => {
    if (!game?.gameId) { setLoading(false); return; }
    setLoading(true); setError(null);
    api.getGameBoxScore(game.gameId)
      .then(raw => { setBoxData(parseBoxScore(raw, game)); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [game?.gameId]);

  if (!game) return null;

  function handlePlayerClick(boxPlayer) {
    const stats = statsRef.current || game?._stats;
    const num  = String(boxPlayer.number || '').trim();
    const norm = s => String(s||'').replace(/\s+/g,' ').trim().toLowerCase();
    const key  = norm(boxPlayer.name || boxPlayer.playerName || '');
    if ('AVG' in boxPlayer) { setInlinePlayer(boxPlayer); return; }
    if ('ERA' in boxPlayer) { setInlinePlayer({ ...boxPlayer, playerId: 'pit_' + (boxPlayer.playerName||boxPlayer.name) }); return; }
    const batter  = stats?.battingSeason?.find(p => String(p.number||'').trim() === num)
                 || stats?.battingSeason?.find(p => norm(p.playerName) === key);
    const pitcher = !batter && (
                    stats?.pitchingSeason?.find(p => String(p.number||'').trim() === num)
                 || stats?.pitchingSeason?.find(p => norm(p.playerName) === key));
    if (batter)  { setInlinePlayer(batter); return; }
    if (pitcher) { setInlinePlayer({ ...pitcher, playerId: 'pit_' + pitcher.playerName }); return; }
    setInlinePlayer({ playerName: boxPlayer.name || boxPlayer.playerName || '(Player)', number: boxPlayer.number, AVG: null });
  }

  const innings  = boxData?.innings || synthInnings(game.bat_R, game.pit_R);
  const teamName = team?.name || 'Us';
  const awayName = game.linkText || 'Opponent';

  return (
    <>
      <div className="drawer-scrim" onClick={inlinePlayer ? () => setInlinePlayer(null) : onClose}/>
      <aside className="drawer" style={{maxWidth: 720}} role="dialog" aria-label={inlinePlayer ? 'Player detail' : 'Box score'}>

        {/* Header — swaps between box score and player view */}
        <div className="drawer-h">
          {inlinePlayer ? (
            <>
              <button className="icon-btn" onClick={() => setInlinePlayer(null)} aria-label="Back to box score"><I.Back/></button>
              <div className="drawer-num">{inlinePlayer.number || '—'}</div>
              <div className="drawer-title">
                <h3>{inlinePlayer.playerName || inlinePlayer.name}</h3>
                <p>{'AVG' in inlinePlayer ? `${inlinePlayer.Games||'—'} G · ${inlinePlayer.PA||0} PA`
                  : 'ERA' in inlinePlayer ? `${inlinePlayer.Appearances||'—'} App · ${inlinePlayer.GS||0} GS`
                  : 'vs ' + game.linkText}</p>
              </div>
            </>
          ) : (
            <div className="drawer-title">
              <h3>vs {game.linkText}</h3>
              <p>{game.date || ''}</p>
            </div>
          )}
          <button className="icon-btn" onClick={onClose}><I.X/></button>
        </div>

        <div className="drawer-body">
          {/* Player detail view — unified batting/pitching toggle */}
          {inlinePlayer && (() => {
            const norm = s => String(s||'').replace(/\s+/g,' ').trim().toLowerCase();
            const name = norm(inlinePlayer.playerName || inlinePlayer.name || '');
            const num  = String(inlinePlayer.number || '').trim();
            const br   = ('AVG' in inlinePlayer ? inlinePlayer : null)
                      || stats?.battingSeason?.find(p => norm(p.playerName) === name)
                      || stats?.battingSeason?.find(p => String(p.number||'').trim() === num);
            const pr   = ('ERA' in inlinePlayer && !('AVG' in inlinePlayer) ? inlinePlayer : null)
                      || stats?.pitchingSeason?.find(p => norm(p.playerName) === name)
                      || stats?.pitchingSeason?.find(p => String(p.number||'').trim() === num);
            return <InlinePlayerToggle br={br} pr={pr} stats={stats}/>;
          })()}

          {/* Box score view */}
          {!inlinePlayer && <>

          {/* Scoreboard */}
          <div className="score-board">
            <div className="score-row">
              <div className={`score-team ${game.bat_R < game.pit_R ? 'dim' : ''}`}>
                <div className="nm">{teamName}</div>
                <div className="runs">{game.bat_R}</div>
              </div>
              <div className="score-vs">{game.Result === 'W' ? 'WIN' : game.Result === 'L' ? 'LOSS' : 'FINAL'}</div>
              <div className={`score-team ${game.pit_R < game.bat_R ? 'dim' : ''}`}>
                <div className="nm">{awayName}</div>
                <div className="runs">{game.pit_R}</div>
              </div>
            </div>
          </div>

          {/* Line score */}
          <div className="card" style={{margin: 0, marginBottom: 16}}>
            <div className="card-h"><h2>Line score</h2></div>
            <div style={{overflowX: 'auto'}}>
              <table className="dt" style={{width:'100%',fontSize:11}}>
                <thead><tr>
                  <th className="first" style={{padding:'5px 6px'}}>Team</th>
                  {innings.map((_, i) => <th key={i} style={{padding:'5px 3px'}}>{i+1}</th>)}
                  <th style={{padding:'5px 4px'}}>R</th><th style={{padding:'5px 4px'}}>H</th>
                </tr></thead>
                <tbody>
                  <tr>
                    <td className="first" style={{fontFamily:'var(--font-sans)',fontWeight:600,padding:'5px 6px'}}>{awayName}</td>
                    {innings.map((inn,i) => <td key={i} style={{padding:'5px 3px'}}>{inn.away}</td>)}
                    <td className="hi" style={{padding:'5px 4px'}}>{game.pit_R}</td>
                    <td style={{padding:'5px 4px'}}>{game.pit_H ?? '—'}</td>
                  </tr>
                  <tr>
                    <td className="first" style={{fontFamily:'var(--font-sans)',fontWeight:600,padding:'5px 6px'}}>{teamName}</td>
                    {innings.map((inn,i) => <td key={i} style={{padding:'5px 3px'}}>{inn.home}</td>)}
                    <td className="hi" style={{padding:'5px 4px'}}>{game.bat_R}</td>
                    <td style={{padding:'5px 4px'}}>{game.bat_H ?? '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {loading && (
            <div style={{display:'flex',alignItems:'center',gap:10,color:'var(--text-3)',fontSize:13,padding:'8px 0'}}>
              <div className="spinner" style={{width:18,height:18}}/>
              Loading box score…
            </div>
          )}
          {error && <div className="banner banner-error">Couldn't load full box score: {error}</div>}

          {/* Per-player batting — from parsed box score if available, else full season roster */}
          {(() => {
            const rows = boxData?.batters?.length > 0
              ? boxData.batters
              : (stats?.battingSeason || []).filter(p => p.PA > 0).sort((a,b) => (a.Probable_Lineup_Slot||99)-(b.Probable_Lineup_Slot||99));
            const fromBox = boxData?.batters?.length > 0;
            if (!rows.length) return null;
            return (
              <div className="card" style={{margin: 0, marginBottom: 16}}>
                <div className="card-h">
                  <h2>{teamName} batting</h2>
                  {!fromBox && <span className="sub">Season totals · click for details</span>}
                </div>
                <div className="t-scroll">
                  <table className="dt" style={{fontSize:11}}>
                    <thead><tr>
                      <th className="first" style={{padding:'5px 6px'}}>Player</th>
                      {fromBox ? <><th style={{padding:'5px 4px'}}>Pos</th><th style={{padding:'5px 4px'}}>AB</th><th style={{padding:'5px 4px'}}>R</th><th style={{padding:'5px 4px'}}>H</th><th style={{padding:'5px 4px'}}>RBI</th><th style={{padding:'5px 4px'}}>BB</th><th style={{padding:'5px 4px'}}>SO</th></>
                               : <><th style={{padding:'5px 4px'}}>AVG</th><th style={{padding:'5px 4px'}}>OBP</th><th style={{padding:'5px 4px'}}>OPS</th><th style={{padding:'5px 4px'}}>AB</th><th style={{padding:'5px 4px'}}>H</th><th style={{padding:'5px 4px'}}>HR</th><th style={{padding:'5px 4px'}}>RBI</th></>}
                    </tr></thead>
                    <tbody>
                      {rows.map((p, i) => {
                        const isSeason = !fromBox;
                        const player = isSeason ? p : p;
                        return (
                          <tr key={i} style={{cursor:'pointer'}} onClick={() => handlePlayerClick(p)}>
                            <td className="first" style={{padding:'5px 6px'}}>
                              <div className="player-cell">
                                <div className="player-num">{isSeason ? p.number : p.number}</div>
                                <span style={{fontWeight:700,textDecoration:'underline',textDecorationColor:'var(--border-strong)'}}>{isSeason ? p.playerName : p.name}</span>
                              </div>
                            </td>
                            {fromBox
                              ? <><td style={{padding:'5px 4px'}}>{p.pos||'—'}</td><td style={{padding:'5px 4px'}}>{p.AB??'—'}</td><td style={{padding:'5px 4px'}}>{p.R??'—'}</td><td style={{padding:'5px 4px'}}>{p.H??'—'}</td><td style={{padding:'5px 4px'}}>{p.RBI??'—'}</td><td style={{padding:'5px 4px'}}>{p.BB??'—'}</td><td style={{padding:'5px 4px'}}>{p.SO??'—'}</td></>
                              : <><td className="rate" style={{padding:'5px 4px'}}>{fmt.avg(p.AVG)}</td><td className="rate" style={{padding:'5px 4px'}}>{fmt.avg(p.OBP)}</td><td className="rate hi" style={{padding:'5px 4px'}}>{fmt.avg(p.OPS)}</td><td style={{padding:'5px 4px'}}>{p.AB}</td><td style={{padding:'5px 4px'}}>{p.H}</td><td style={{padding:'5px 4px'}}>{p.HR}</td><td style={{padding:'5px 4px'}}>{p.RBI}</td></>
                            }
                          </tr>
                        );
                      })}
                      {fromBox && (
                        <tr style={{background:'var(--surface-2)'}}>
                          <td className="first" style={{fontFamily:'var(--font-sans)',fontWeight:700,padding:'5px 6px'}}>Totals</td>
                          <td style={{padding:'5px 4px'}}></td>
                          <td style={{padding:'5px 4px'}}>{game.bat_AB}</td><td style={{padding:'5px 4px'}}>{game.bat_R}</td><td style={{padding:'5px 4px'}}>{game.bat_H}</td>
                          <td style={{padding:'5px 4px'}}>{game.bat_RBI}</td><td style={{padding:'5px 4px'}}>{game.bat_BB}</td><td style={{padding:'5px 4px'}}>{game.bat_SO}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Per-player pitching — from parsed box score if available, else full season roster */}
          {(() => {
            const rows = boxData?.pitchers?.length > 0
              ? boxData.pitchers
              : (stats?.pitchingSeason || []).filter(p => p.Appearances > 0).sort((a,b) => (b.IP||0)-(a.IP||0));
            const fromBox = boxData?.pitchers?.length > 0;
            if (!rows.length) return null;
            return (
              <div className="card" style={{margin: 0, marginBottom: 16}}>
                <div className="card-h">
                  <h2>{teamName} pitching</h2>
                  {!fromBox && <span className="sub">Season totals · click for details</span>}
                </div>
                <div className="t-scroll">
                  <table className="dt" style={{fontSize:11}}>
                    <thead><tr>
                      <th className="first" style={{padding:'5px 6px'}}>Pitcher</th>
                      {fromBox ? <><th style={{padding:'5px 4px'}}>IP</th><th style={{padding:'5px 4px'}}>H</th><th style={{padding:'5px 4px'}}>R</th><th style={{padding:'5px 4px'}}>ER</th><th style={{padding:'5px 4px'}}>BB</th><th style={{padding:'5px 4px'}}>SO</th><th style={{padding:'5px 4px'}}>ERA</th></>
                               : <><th style={{padding:'5px 4px'}}>ERA</th><th style={{padding:'5px 4px'}}>WHIP</th><th style={{padding:'5px 4px'}}>IP</th><th style={{padding:'5px 4px'}}>K</th><th style={{padding:'5px 4px'}}>BB</th></>}
                    </tr></thead>
                    <tbody>
                      {rows.map((p, i) => {
                        const isSeason = !fromBox;
                        const era = fromBox && p.IP > 0 ? ((p.ER * 7) / p.IP).toFixed(2) : '—';
                        return (
                          <tr key={i} style={{cursor:'pointer'}} onClick={() => handlePlayerClick(p)}>
                            <td className="first" style={{padding:'5px 6px'}}>
                              <div className="player-cell">
                                <div className="player-num">{p.number}</div>
                                <span style={{fontWeight:700,textDecoration:'underline',textDecorationColor:'var(--border-strong)'}}>{isSeason ? p.playerName : p.name}</span>
                              </div>
                            </td>
                            {fromBox
                              ? <><td className="mono" style={{padding:'5px 4px'}}>{fmt.ip(p.IP)}</td><td style={{padding:'5px 4px'}}>{p.H??'—'}</td><td style={{padding:'5px 4px'}}>{p.R??'—'}</td><td style={{padding:'5px 4px'}}>{p.ER??'—'}</td><td style={{padding:'5px 4px'}}>{p.BB??'—'}</td><td style={{padding:'5px 4px'}}>{p.SO??'—'}</td><td className="mono" style={{padding:'5px 4px'}}>{era}</td></>
                              : <><td className="rate" style={{padding:'5px 4px'}}>{fmt.dec(p.ERA,2)}</td><td className="rate" style={{padding:'5px 4px'}}>{fmt.dec(p.WHIP,2)}</td><td style={{padding:'5px 4px'}}>{fmt.ip(p.IP)}</td><td style={{padding:'5px 4px'}}>{p.SO}</td><td style={{padding:'5px 4px'}}>{p.BB}</td></>
                            }
                          </tr>
                        );
                      })}
                      {fromBox && (
                        <tr style={{background:'var(--surface-2)'}}>
                          <td className="first" style={{fontFamily:'var(--font-sans)',fontWeight:700,padding:'5px 6px'}}>Totals</td>
                          <td className="mono" style={{padding:'5px 4px'}}>{fmt.ip(game.pit_IP)}</td>
                          <td style={{padding:'5px 4px'}}>{game.pit_H}</td><td style={{padding:'5px 4px'}}>{game.pit_R}</td><td style={{padding:'5px 4px'}}>{game.pit_ER}</td>
                          <td style={{padding:'5px 4px'}}>{game.pit_BB}</td><td style={{padding:'5px 4px'}}>{game.pit_SO}</td>
                          <td className="mono" style={{padding:'5px 4px'}}>{game.pit_IP > 0 ? ((game.pit_ER*7)/game.pit_IP).toFixed(2) : '—'}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          </> /* end box score view */}
        </div>
      </aside>
    </>
  );
}

/* ─── Shared batting/pitching toggle (used by both drawers) ─ */
function InlinePlayerToggle({ br, pr, stats }) {
  const [view, setView] = useState('batting');
  const hasPitching = !!(pr?.Appearances > 0);
  return (
    <>
      <div style={{display:'flex', borderBottom:'1px solid var(--border)', marginBottom:0, marginLeft:-16, marginRight:-16, paddingLeft:16}}>
        {['batting','pitching'].map(v => (
          <button key={v} onClick={()=>setView(v)} style={{
            padding:'10px 16px 10px 0', fontSize:12, fontWeight:700,
            textTransform:'uppercase', letterSpacing:'0.08em',
            color: view===v ? 'var(--text)' : 'var(--text-3)',
            borderBottom: view===v ? '2px solid var(--red)' : '2px solid transparent',
            transition:'color 120ms, border-color 120ms',
          }}>
            {v}
          </button>
        ))}
      </div>
      {view === 'batting' && (
        br
          ? <><BatterDetail p={br} stats={stats}/><FieldingDetail p={br} stats={stats}/></>
          : <div className="empty" style={{marginTop:24}}><h3>No batting appearances</h3></div>
      )}
      {view === 'pitching' && (
        hasPitching
          ? <PitcherDetail p={pr}/>
          : <div className="empty" style={{marginTop:24}}><h3>No pitching appearances</h3></div>
      )}
    </>
  );
}

/* ─── Parse raw GC box score ──────────────────────────── */
function parseBoxScore(raw, game) {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw.body || raw;
  const teamIds = Object.keys(body).filter(k => !['ok','gameId','linkText','scheduleUrl'].includes(k));
  if (!teamIds.length) return null;
  const payload = body[teamIds[0]];
  if (!payload) return null;

  const rosterMap = new Map();
  for (const p of payload.players || []) {
    if (p.id) {
      const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || '(Player)';
      rosterMap.set(p.id, { name, number: String(p.number || '').trim() });
    }
  }

  const batters = [], pitchers = [];

  for (const group of payload.groups || []) {
    const { category, stats: rows = [] } = group;
    if (category === 'lineup') {
      const seen = new Set();
      for (const row of rows) {
        if (!row.player_id || !row.is_primary || seen.has(row.player_id)) continue;
        seen.add(row.player_id);
        const meta = rosterMap.get(row.player_id) || { name: '(Player)', number: '' };
        const s = row.stats || {};
        batters.push({ name: meta.name, number: meta.number, pos: extractPos(row.player_text), AB: nv(s.AB), R: nv(s.R), H: nv(s.H), RBI: nv(s.RBI), BB: nv(s.BB), SO: nv(s.SO) });
      }
    }
    if (category === 'pitching') {
      const seen = new Set();
      for (const row of rows) {
        if (!row.player_id || seen.has(row.player_id)) continue;
        seen.add(row.player_id);
        const meta = rosterMap.get(row.player_id) || { name: '(Player)', number: '' };
        const s = row.stats || {};
        pitchers.push({ name: meta.name, number: meta.number, IP: nv(s.IP), H: nv(s.H), R: nv(s.R), ER: nv(s.ER), BB: nv(s.BB), SO: nv(s.SO) });
      }
    }
  }

  return { batters, pitchers, innings: synthInnings(game.bat_R, game.pit_R) };
}

function nv(x) { const v = Number(x); return Number.isFinite(v) ? v : 0; }
function extractPos(playerText) { const m = (playerText || '').match(/\(([^)]+)\)/); return m ? m[1].split(',')[0].trim() : ''; }

function synthInnings(homeR, awayR) {
  const n = 7;
  const innings = Array.from({ length: n }, () => ({ home: 0, away: 0 }));
  let h = homeR, a = awayR;
  while (h > 0) { for (let i = 0; i < n && h > 0; i++) { if (Math.random() > 0.55) { innings[i].home += 1; h--; } } }
  while (a > 0) { for (let i = 0; i < n && a > 0; i++) { if (Math.random() > 0.55) { innings[i].away += 1; a--; } } }
  return innings;
}
